import { useForm } from 'react-hook-form'

import { useMyMonitoringCreateSystemUser } from '@apps-common/apis/my-monitoring/users'
import { useCreateSiteNamedUser } from '@apps-common/apis/portal/users'
import { ButtonRoundedSm } from '@apps-common/components/elements/button'
import { InputSM } from '@apps-common/components/elements/form'
import { VertSpacerMd } from '@apps-common/components/layout'
import { ModalBody, ModalFooter } from '@apps-common/components/modals/modal'
import { ModalDismiss } from '@apps-common/config/modal'
import { FORM_DEFAULT_REQUIRED } from '@apps-common/config/react-hook-form'
import { useProjectHook } from '@apps-common/hooks/useProjectHook'
import { ISiteNamedUserModel } from '@apps-common/types/portal/users'

interface IProps {
  uId: string
  closeCb: (role: string) => void
}

export const SystemUserAddNewForm: React.FC<IProps> = ({ uId, closeCb }) => {
  const useCreateSystemUserHook = useProjectHook(useCreateSiteNamedUser, useMyMonitoringCreateSystemUser)

  const { mutate: create, isPending: createIsPending } = useCreateSystemUserHook(true, () => closeCb(ModalDismiss.Confirm))

  const {
    register,
    handleSubmit,
    formState: { isSubmitting, errors }
  } = useForm<ISiteNamedUserModel>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      unitNamedUserId: 0,
      unitId: uId,
      firstName: '',
      lastName: '',
      profileImage: '',
      zone: '',
      mapPartition: false,
      partition: '',
      userName: '',
      editDate: new Date().toISOString()
    }
  })

  const submit = (values: ISiteNamedUserModel) => {
    if (values?.partition && values.partition?.length > 0) {
      values.mapPartition = true
    }

    create({ model: values })
  }

  const isLoading = isSubmitting || createIsPending

  return (
    <>
      <ModalBody classes='!pt-[90px]'>
        <form noValidate id='system-user-create-form' onSubmit={handleSubmit(submit)}>
          <VertSpacerMd />
          <div className='grid sm:grid-cols-2 gap-[10px]'>
            <InputSM
              key='firstName'
              type='text'
              placeholder='First name'
              label='First name'
              id='firstName'
              showLabel
              disabled={isLoading}
              registration={register('firstName', {
                required: FORM_DEFAULT_REQUIRED,
                maxLength: {
                  message: 'No longer than 15 characters',
                  value: 15
                }
              })}
              errors={errors.firstName}
            />
            <InputSM
              key='lastName'
              type='text'
              placeholder='Last name'
              label='Last name'
              id='lastName'
              showLabel
              disabled={isLoading}
              registration={register('lastName', {
                required: FORM_DEFAULT_REQUIRED,
                maxLength: {
                  message: 'No longer than 15 characters',
                  value: 15
                }
              })}
              errors={errors.lastName}
            />
          </div>
          <InputSM
            key='zone'
            type='text'
            placeholder='1 to 999'
            label='User number (Zone)'
            id='zone'
            showLabel
            disabled={isLoading}
            registration={register('zone', {
              required: FORM_DEFAULT_REQUIRED,
              pattern: {
                value: /^[0-9]{3}$/,
                message: '3 numerical characters only e.g. 001'
              }
            })}
            errors={errors.zone}
          />
          <InputSM
            key='partition'
            type='text'
            placeholder='0 to 99'
            label='User partition (If required)'
            id='partition'
            showLabel
            disabled={isLoading}
            registration={register('partition', {
              required: false,
              pattern: {
                value: /^[0-9]{2}$/,
                message: '2 numerical characters only e.g. 01'
              }
            })}
            errors={errors.partition}
          />
        </form>
      </ModalBody>
      <ModalFooter>
        <div className='flex items-center justify-between gap-2'>
          <ButtonRoundedSm type='submit' form='system-user-create-form' loading={isLoading} text='Create' className='action-success' />
          <ButtonRoundedSm
            type='button'
            loading={isLoading}
            text='Cancel'
            className='action-neutral'
            callback={() => closeCb(ModalDismiss.Cancel)}
          />
        </div>
      </ModalFooter>
    </>
  )
}
